import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import type { ChatBotMessage } from "@/shared/types/chat";
import { ChatBotModeType } from "@/generated/fastapi";

export interface RagSlice {
  isChatInitialized: boolean;
  // Chat messages management
  chatMessages: ChatBotMessage[];
  setChatMessages: (messages: ChatBotMessage[]) => void;
  addChatMessage: (message: ChatBotMessage) => void;
  updateChatMessage: (id: number, updates: Partial<ChatBotMessage>) => void;
  deleteChatMessage: (id: number) => void;
  addChatReaction: (id: number, emoji: string) => void;
  resetChat: () => void;
  // Selection management
  selectedChatMessages: number[];
  addSelectedChatMessage: (id: number) => void;
  removeSelectedChatMessage: (id: number) => void;
  clearSelectedChatMessages: () => void;
  toggleSelectedChatMessage: (id: number) => void; // If selected, remove it; if not selected, add it.
  countSelectedChatMessages: () => number;
  // Session management
  chatSessionId: string | null;
  setChatSessionId: (sessionId: string | null) => void;
  // Mode management
  chatMode: ChatBotModeType | null;
  setChatMode: (mode: ChatBotModeType | null) => void;
  // Loading states
  waitingForBot: boolean;
  setWaitingForBot: (waiting: boolean) => void;
  loadingKnowledge: boolean;
  setLoadingKnowledge: (loading: boolean) => void;
  clearingRAG: boolean;
  setClearingRAG: (clearing: boolean) => void;
  preparingChat: boolean;
  setPreparingChat: (preparing: boolean) => void;
}

type PersistableRagState = Pick<
  RagSlice,
  "chatMessages" | "selectedChatMessages" | "chatSessionId" | "chatMode"
>;

export const createRagSlice: StateCreator<GlobalState, [], [], RagSlice> = (
  set,
  get,
) => ({
  isChatInitialized: false,

  chatMessages: [],
  setChatMessages: (messages) => set({ chatMessages: messages }),
  addChatMessage: (message) =>
    set((state) => ({ chatMessages: [...state.chatMessages, message] })),
  updateChatMessage: (id, updates) =>
    set((state) => ({
      chatMessages: state.chatMessages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg,
      ),
    })),
  deleteChatMessage: (id) =>
    set((state) => ({
      chatMessages: state.chatMessages.filter((msg) => msg.id !== id),
    })),

  addChatReaction: (id, emoji) =>
    set((state) => ({
      chatMessages: state.chatMessages.map((msg) =>
        msg.id === id
          ? { ...msg, reactions: [...(msg.reactions || []), emoji] }
          : msg,
      ),
    })),

  resetChat: () =>
    set({
      isChatInitialized: true,
      chatMessages: [],
      selectedChatMessages: [],
      chatSessionId: null,
      chatMode: null,
      waitingForBot: false,
      loadingKnowledge: false,
      clearingRAG: false,
      preparingChat: false,
    }),

  // Selection management
  selectedChatMessages: [],
  addSelectedChatMessage: (id) =>
    set((state) => ({
      selectedChatMessages: Array.from(
        new Set([...state.selectedChatMessages, id]),
      ),
    })),
  removeSelectedChatMessage: (id) =>
    set((state) => ({
      selectedChatMessages: state.selectedChatMessages.filter(
        (msgId) => msgId !== id,
      ),
    })),
  clearSelectedChatMessages: () => set({ selectedChatMessages: [] }),
  toggleSelectedChatMessage: (id) =>
    set((state) => ({
      selectedChatMessages: state.selectedChatMessages.includes(id)
        ? state.selectedChatMessages.filter((msgId) => msgId !== id)
        : [...state.selectedChatMessages, id],
    })),
  countSelectedChatMessages: () => {
    const state = get();
    return state.selectedChatMessages.length;
  },

  // Session management
  chatSessionId: null,
  setChatSessionId: (sessionId) => set({ chatSessionId: sessionId }),

  // Mode management
  chatMode: null,
  setChatMode: (mode) => set({ chatMode: mode }),

  // Loading states
  waitingForBot: false,
  setWaitingForBot: (waiting) => set({ waitingForBot: waiting }),
  loadingKnowledge: false,
  setLoadingKnowledge: (loading) => set({ loadingKnowledge: loading }),
  clearingRAG: false,
  setClearingRAG: (clearing) => set({ clearingRAG: clearing }),
  preparingChat: false,
  setPreparingChat: (preparing) => set({ preparingChat: preparing }),
});

export function pickPersistedRagState(state: PersistableRagState) {
  return {
    chatMessages: state.chatMessages,
    selectedChatMessages: state.selectedChatMessages,
    chatSessionId: state.chatSessionId,
    chatMode: state.chatMode,
  };
}
